import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Box, Typography, Paper, Grid, Button, Table, TableHead, TableCell, TableRow, TableBody, TableContainer, alpha, TablePagination } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { capitalize } from '@mui/material';

export const PaymentDetails = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { transaction } = location.state || {};
    const [ page, setPage ] = React.useState( 0 );
    const [ rowsPerPage, setRowsPerPage ] = React.useState( 5 );

    const handleChangePage = ( _: unknown, newPage: number ) => {
        setPage( newPage );
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setRowsPerPage( parseInt( event.target.value, 10 ) );
        setPage( 0 );
    };

    if ( !transaction ) {
        return <h2>No payment data found</h2>;
    }
    const statusMap: Record<
        "PAYMENT_COMPLETED" | "PAYMENT_IN_ESCROW" | "REFUND_TO_BUYER" | "PAYMENT_TO_SELLER",
        string
    > = {
        PAYMENT_COMPLETED: "Payment in escrow",
        PAYMENT_IN_ESCROW: "Payment in escrow",
        REFUND_TO_BUYER: "Payment refund to buyer",
        PAYMENT_TO_SELLER: "Payment transfer to seller",
    };
    const formatStatus = ( status: string ) => {
        if ( !status ) return "";

        return status
            .toLowerCase()                // buyer_satisfied
            .replace( /_/g, " " )           // buyer satisfied
            .replace( /\b\w/g, ( c ) => c.toUpperCase() ); // Buyer Satisfied
    };

    const formatDate = ( date: string ) => {
        return new Intl.DateTimeFormat( "en-US", {
            month: "short",
            day: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            hour12: false
        } ).format( new Date( date ) );
    };
    const paginatedHistory = transaction.history?.slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
    );
    return (
        <>
            <Box sx={ { p: 3 } }>
                {/* Back Button + Title */ }
                <Box display="flex" alignItems="center" mb={ 3 } gap={ 2 }>
                    <Button
                        variant="outlined"
                        startIcon={ <ArrowBackIcon /> }
                        onClick={ () => navigate( '/payment-history' ) }
                    >
                        Back
                    </Button>

                    <Typography variant="h4" fontWeight="bold">
                        Payment Details – { transaction.id }
                    </Typography>
                </Box>

                {/* Details Card */ }
                <Paper sx={ { p: 3, borderRadius: 3 } }>
                    <Grid container spacing={ 2 }>

                        {/* USER */ }
                        <Grid item xs={ 12 } md={ 6 }>
                            <Typography variant="subtitle1">
                                <strong>User Name:</strong>{ " " }
                                { capitalize( transaction.user?.firstName || "" ) }{ " " }
                                { capitalize( transaction.user?.lastName || "" ) }
                                { transaction.user?.deletedAt && " (User Disabled)" }
                            </Typography>

                            <Typography variant="subtitle1">
                                <strong>Payment ID:</strong> { transaction.paymentId }
                            </Typography>

                            <Typography variant="subtitle1">
                                <strong>Date Created:</strong> { formatDate( transaction.createdAt ) }
                            </Typography>

                            <Typography variant="subtitle1">
                                <strong>Amount:</strong> ${ transaction.amount }
                            </Typography>

                            <Typography variant="subtitle1">
                                <strong>Payment Type:</strong> { transaction.paymentType === "Paid_For_Item" ? "Paid for item" : "Locker charges" }
                            </Typography>

                            <Typography variant="subtitle1">
                                <strong>Payment Status:</strong> { transaction.status }
                            </Typography>
                        </Grid>

                        {/* OFFER DETAILS */ }
                        <Grid item xs={ 12 } md={ 6 }>
                            <Typography variant="subtitle1">
                                <strong>Offer Title:</strong> { transaction.offer?.title }
                            </Typography>

                            <Typography variant="subtitle1">
                                <strong>Offer ID:</strong> { transaction.offer?.id }
                            </Typography>

                            <Typography variant="subtitle1">
                                <strong>Offer Status:</strong> { formatStatus( transaction.offer?.status ) }
                            </Typography>

                            <Typography variant="subtitle1">
                                <strong>Offer Payment Status:</strong> { "" } { transaction.offer?.paymentStatus &&
                                    statusMap[ transaction.offer?.paymentStatus as keyof typeof statusMap ]
                                    ? statusMap[ transaction.offer?.paymentStatus as keyof typeof statusMap ]
                                    : "Pending"
                                }
                            </Typography>
                        </Grid>

                    </Grid>

                </Paper>
            </Box>
            {/* HISTORY TABLE */ }
            <Box sx={ { mt: 4 } }>
                <Typography variant="h5" fontWeight="bold" mb={ 2 }>
                    Payment History
                </Typography>

                { transaction.history?.length > 0 ? (
                   <TableContainer
                                 component={ Paper }
                                 sx={ {
                                   borderRadius: 3,
                                   overflow: "hidden",

                                 } }
                               >
                        <Table>
                            <TableHead>
                                <TableRow
                                    sx={ {
                                        backgroundColor: "primary.main",
                                        "& th": {
                                            color: "white",
                                            fontWeight: 600,
                                            fontSize: "0.875rem",
                                            textTransform: "uppercase",
                                            letterSpacing: "0.5px",
                                        },
                                    } }
                                >
                                    <TableCell align="center">Date</TableCell>
                                    <TableCell align="center">Step</TableCell>
                                    <TableCell align="center">Payment ID</TableCell>
                                </TableRow>
                            </TableHead>

                            <TableBody>
                                { paginatedHistory.map( ( item: any, index: number ) => (

                                    <TableRow
                                        key={ index }
                                        sx={ {
                                            "&:hover": {
                                                backgroundColor: ( theme ) =>
                                                    alpha( theme.palette.primary.main, 0.04 ),
                                            },
                                            transition: "background-color 0.2s ease",
                                        } }
                                    >
                                        {/* Date */ }
                                        <TableCell align="center">
                                            <Typography variant="body2" fontWeight={ 500 }>
                                                { formatDate( item.date ) }
                                            </Typography>
                                        </TableCell>

                                        {/* Step */ }
                                        <TableCell align="center">
                                            <Typography
                                                variant="body2"
                                                sx={ {
                                                    textTransform: "capitalize",
                                                    fontWeight: 500,
                                                } }
                                            >
                                                { item.step.replace( /_/g, " " ) }
                                            </Typography>
                                        </TableCell>

                                        {/* Payment Object ID */ }
                                        <TableCell align="center">
                                            <Typography
                                                variant="body2"
                                                sx={ {
                                                    fontFamily: "monospace",
                                                    fontSize: "0.85rem",
                                                } }
                                            >
                                                { item.paymentObject?.id || "N/A" }
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                ) ) }
                            </TableBody>
                        </Table>
                        <TablePagination
                            component="div"
                            rowsPerPageOptions={ [ 5, 10, 25 ] }
                            count={ transaction.history.length }
                            rowsPerPage={ rowsPerPage }
                            page={ page }
                            onPageChange={ handleChangePage }
                            onRowsPerPageChange={ handleChangeRowsPerPage }
                            sx={ {
                                borderTop: "1px solid",
                                borderColor: "divider",
                            } }
                        />

                    </TableContainer>

                ) : (
                    <Typography>No history available</Typography>
                ) }

            </Box>
        </>
    );
};
